import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Chip,
  Divider,
  ProjectCardSkeletonGrid,
  Button,
  TextInput,
  EmptyState,
  PageHeader,
} from "../../../components/shared";
import { DashboardIcon, SearchIcon, CloseIcon } from "../../../assets/icons";
import {
  HiOutlineClipboardDocumentList,
  HiOutlineClock,
  HiOutlineRocketLaunch,
  HiOutlineCheckCircle,
  HiOutlineEnvelope,
  HiOutlineLockClosed,
} from "react-icons/hi2";
import { useProjectsList } from "../../../lib/react-query/projects";
import ProjectCard from "../../../components/Projects/ProjectCard";
import CreateProjectModal from "../../../components/Projects/CreateProjectModal";
import useQueryAction from "../../../hooks/useQueryAction";
import { useIsClient } from "../../../hooks/useClientRole";

// Animation variants defined outside component to prevent recreation
const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { 
    opacity: 1, 
    y: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24
    }
  }
};

const Projects = () => {
  const [formData, setFormData] = useState({ search: "" });
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState(formData.search);
  const { status } = useParams();
  const view = (status || "all").toLowerCase();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isClient: isClientModeActive } = useIsClient();

  const teamIdParam = searchParams.get("team_id");
  const teamId = teamIdParam ? Number(teamIdParam) : null;
  const teamSearch = teamId ? `?team_id=${teamId}` : "";

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(formData.search);
    }, 500);
    return () => clearTimeout(handler);
  }, [formData.search]);

  useEffect(() => {
    if (isClientModeActive) return;
    const prompt = searchParams.get("prompt_project_create");
    if (prompt) {
      setOpenCreateModal(true);
      searchParams.delete("prompt_project_create");
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams, isClientModeActive]);

  useQueryAction("create_project", () => {
    if (!isClientModeActive) setOpenCreateModal(true);
  });

  const statusMap = {
    all: null,
    pending: "PENDING",
    active: "ACTIVE",
    done: "DONE",
    invited: "INVITED",
  };

const emptyStateConfig = {
  all: {
    title: "No projects yet",
    description: "Create your first project to track deals, share files, and collaborate with your team.",
  },
  pending: {
    title: "No pending projects",
    description: "Projects waiting to start will appear here. Create a new project to begin planning.",
  },
  active: {
    title: "No active projects",
    description: "Projects in progress will show here. Start a project or move one from pending to active.",
  },
  done: {
    title: "No completed projects",
    description: "Finished projects will be listed here. Complete a project to see it in your archive.",
  },
  invited: {
    title: "No pending invitations",
    description: "Projects you've been invited to will appear here. Ask a project manager to invite you.",
  },
};



const emptyShowCta = (view) => ["all", "pending", "active"].includes(view);

const emptyIconMap = {
  all: HiOutlineClipboardDocumentList,
  pending: HiOutlineClock,
  active: HiOutlineRocketLaunch,
  done: HiOutlineCheckCircle,
  invited: HiOutlineEnvelope,
};

  const statusParam = statusMap[view] || "";

  const { data: projectsData, isLoading: projectsLoading } = useProjectsList(
    {
      search: debouncedSearch,
      ordering: "-updated_at",
      status: statusParam,
      team_id: teamId,
      page: 1,
      page_size: 12,
    },
    true,
  );
  const {
    data: invitedProjectsData,
    isLoading: invitedProjectsLoading,
  } = useProjectsList(
    {
      search: debouncedSearch,
      ordering: "-updated_at",
      status: "INVITED",
      team_id: teamId,
      page: 1,
      page_size: 12,
    },
    view === "all",
  );

  const items = useMemo(() => {
    const baseItems = projectsData?.items || [];
    if (view !== "all") return baseItems;

    const invitedItems = invitedProjectsData?.items || [];
    if (!invitedItems.length) return baseItems;

    const merged = [...baseItems];
    const seen = new Set(baseItems.map((project) => project.id));
    invitedItems.forEach((project) => {
      if (!seen.has(project.id)) merged.push(project);
    });
    return merged;
  }, [projectsData?.items, invitedProjectsData?.items, view]);
  const isPageLoading =
    projectsLoading || (view === "all" && invitedProjectsLoading);

  return (
    <div className="flex flex-col h-full bg-bggrey">
      <div className="bg-white">
        <PageHeader
          className="mb-0 px-4 md:px-6 py-4"
          breadcrumbs={[
            { label: "Home", href: "/dashboard" },
            { label: "Projects" },
          ]}
          title={
            <span className="flex items-center gap-3">
              <DashboardIcon width={24} height={24} className="text-primary" />
              {isClientModeActive ? "Your Projects" : "Projects"}
            </span>
          }
          description={
            isClientModeActive
              ? "View the projects you've been invited to follow."
              : undefined
          }
          actions={
            !isClientModeActive && (
              <Button
                className="rounded-lg font-medium w-auto bg-primary"
                color="primary"
                size="md"
                onClick={() => setOpenCreateModal(true)}
              >
                Start New Project
              </Button>
            )
          }
        />
        <Divider />
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between px-4 md:px-6 py-4 gap-4 bg-surface/80">
          {!isClientModeActive && formData?.search ? (
            ""
          ) : !isClientModeActive ? (
            <div className="flex items-center flex-wrap gap-3">
              <button type="button" onClick={() => navigate(`/projects${teamSearch}`)}>
                <Chip cross={false} variant={view === "all" ? "filled" : "outlined"} text="All" />
              </button>
              <button type="button" onClick={() => navigate(`/projects/pending${teamSearch}`)}>
                <Chip cross={false} variant={view === "pending" ? "filled" : "outlined"} text="Pending" />
              </button>
              <button type="button" onClick={() => navigate(`/projects/active${teamSearch}`)}>
                <Chip cross={false} variant={view === "active" ? "filled" : "outlined"} text="Active" />
              </button>
              <button type="button" onClick={() => navigate(`/projects/done${teamSearch}`)}>
                <Chip cross={false} variant={view === "done" ? "filled" : "outlined"} text="Done" />
              </button>
              <button type="button" onClick={() => navigate(`/projects/invited${teamSearch}`)}>
                <Chip cross={false} variant={view === "invited" ? "filled" : "outlined"} text="Client" />
              </button>
            </div>
          ) : null}
          <div className="w-full lg:w-80">
            <TextInput
              label=""
              type="text"
              name="search"
              placeholder="Search projects..."
              value={formData?.search || ""}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  [e.target.name]: e.target.value,
                }))
              }
              startContent={
                <SearchIcon width={16} height={16} className="text-darkgrey" />
              }
              endContent={
                formData?.search ? (
                  <button
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, search: "" }))}
                    className="hover:bg-lightgrey rounded-full p-0.5 transition-colors"
                  >
                    <CloseIcon width={14} height={14} className="text-darkgrey" />
                  </button>
                ) : null
              }
            />
          </div>
        </div>
      </div>

      <div className="flex-1 pt-6">
        {isPageLoading ? (
          <ProjectCardSkeletonGrid count={9} />
        ) : items?.length > 0 ? (
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 sm:grid-cols-2 base:grid-cols-3 gap-6"
          >
            {items.map((project) => (
              <ProjectCard key={project.id} project={project} variants={itemVariants} />
            ))}
          </motion.div>
        ) : (
          <EmptyState
            icon={isClientModeActive ? HiOutlineLockClosed : (emptyIconMap[view] || HiOutlineClipboardDocumentList)}
            title={isClientModeActive ? "No Projects Available" : (emptyStateConfig[view]?.title || "No projects yet")}
            description={
              isClientModeActive
                ? "Your account is limited to invited projects. You'll see your project here once a manager assigns or activates one."
                : (emptyStateConfig[view]?.description || "Create your first project to track deals, share files, and collaborate with your team.")
            }
            actionText={isClientModeActive || !emptyShowCta(view) ? undefined : "Start New Project"}
            onAction={isClientModeActive || !emptyShowCta(view) ? undefined : () => setOpenCreateModal(true)}
            className="w-full"
          />
        )}
      </div>

      {!isClientModeActive && (
        <CreateProjectModal
          isOpen={openCreateModal}
          onClose={() => {
            setOpenCreateModal(false);
            if (searchParams.has("create_project")) {
              searchParams.delete("create_project");
              setSearchParams(searchParams);
            }
          }}
        />
      )}
    </div>
  );
};

export default Projects;
